import React, { useState, useMemo, useEffect, useRef } from 'react';
import { useLatest } from 'react-use';
import { debounce, noop } from 'lodash';
import { Select, type SelectProps } from 'antd';

export interface BaseSelectProps extends SelectProps {
  delay?: number;
  enableFreeInput?: boolean;
  onRemoteSearch?: (value: string) => Promise<{ label: string; value: string }[]> | undefined;
}

export const SearchSelect = (props: BaseSelectProps) => {
  const { mode, enableFreeInput = false, delay = 100, onRemoteSearch, ...restProps } = props;
  const [loading, setLoading] = useState(false);
  const onRemoteSearchRef = useLatest(onRemoteSearch || (noop as any));
  const searchIdRef = useRef(0);
  const [options, setOptions] = useState<SelectProps['options']>(
    props.options ? [...props.options] : [],
  );

  useEffect(() => {
    // reset options when props.options change
    setOptions(props.options ? [...props.options] : []);
  }, [props.options]);

  const handleOnSearch = useMemo(() => {
    const handle = debounce(async (text: string) => {
      const newOptions =
        enableFreeInput && text
          ? [
              {
                value: text,
                label: text,
              },
            ]
          : [];

      const searchId = ++searchIdRef.current;
      setLoading(true);
      try {
        const remoteData = await Promise.resolve(onRemoteSearchRef.current(text));
        newOptions.push(...(remoteData || []));
      } catch (e) {
        // nothing
      }
      if (searchId !== searchIdRef.current) {
        return;
      }
      setOptions(newOptions);
      setLoading(false);
    }, delay);
    return handle;
  }, [delay, enableFreeInput, onRemoteSearchRef]);

  return (
    <Select
      labelInValue
      allowClear
      showSearch
      mode={mode}
      loading={loading}
      options={options}
      placeholder="Input value"
      onSearch={handleOnSearch}
      onBlur={() => {
        handleOnSearch.cancel();
        setLoading(false);
      }}
      {...restProps}
    />
  );
};
